import { Location, NavigateFunction } from "react-router-dom"

export function capitalize(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

export const phoneNumberAutoFormat = (phoneNumber: string): string => {
  const number = phoneNumber.trim().replace(/[^0-9]/g, "")
  if (number[0] === "1") return number.slice(1)
  if (number.length < 4) return number
  if (number.length < 7) return number.replace(/(\d{3})(\d{1})/, "$1-$2")
  return number.replace(/(\d{3})(\d{3})(\d{1})/, "$1-$2-$3")
}

export const cleanPhoneNumber = (phoneNumber: string) => {
  if (!phoneNumber) return ""
  return phoneNumber.replace(/\D/g, "")
}

export const navigation: { navigate: null | NavigateFunction; location: null | Location } = {
  navigate: null,
  location: null,
}

export const showErroPage = () => {
  if (navigation.navigate) navigation.navigate("/app/error")
}

export const doPasswordsMatch = (password: string, confirmPassword: string) => {
  if (password !== confirmPassword) {
    alert("Passwords do not match")
    return false
  }
  return true
}

export function readLatLng(
  latLng: google.maps.LatLng | google.maps.LatLngLiteral,
): { lat: number; lng: number } {
  const lat = typeof latLng.lat === "function" ? latLng.lat() : latLng.lat
  const lng = typeof latLng.lng === "function" ? latLng.lng() : latLng.lng
  return { lat: Number(lat), lng: Number(lng) }
}

export const getCoordinates = async (
  address: string,
): Promise<{ lat: number; lng: number } | null> => {
  if (typeof google === "undefined" || !google.maps?.Geocoder) {
    alert("Maps is not loaded yet. Please wait a moment and try Search again.")
    return null
  }

  const geocoder = new google.maps.Geocoder()
  try {
    const { results } = await geocoder.geocode({ address })
    if (!results?.length) {
      alert("The address appears to be invalid. Please check it and try again.")
      return null
    }
    const { lat, lng } = readLatLng(results[0].geometry.location)
    if (!Number.isFinite(lat) || !Number.isFinite(lng) || (lat === 0 && lng === 0)) {
      alert("The address appears to be invalid. Please check it and try again.")
      return null
    }
    return { lat, lng }
  } catch (error: any) {
    const status = error?.code || error?.message || "UNKNOWN_ERROR"
    alert(
      status === "ZERO_RESULTS"
        ? "The address appears to be invalid. Please check it and try again."
        : `Geocode was not successful: ${status}`,
    )
    return null
  }
}

export function isEmpty(obj: {} | [] | null | undefined) {
  if (!obj) return true
  return Object.keys(obj).length === 0
}

export function sortByAttribute(arr: any[], attribute: string, ascending: boolean = true) {
  if (!arr) return []
  if (ascending) {
    const sortedArr = arr.sort((a: { [key: string]: any }, b: { [key: string]: any }) => {
      if (a[attribute] < b[attribute]) return -1
      if (a[attribute] > b[attribute]) return 1
      return 0
    })
    return sortedArr
  } else {
    const sortedArr = arr.sort((a: { [key: string]: any }, b: { [key: string]: any }) => {
      if (a[attribute] > b[attribute]) return -1
      if (a[attribute] < b[attribute]) return 1
      return 0
    })
    return sortedArr
  }
}

export function getTimeDifference(date1: Date, date2: Date) {
  // Calculate the difference in milliseconds
  const diff = Math.abs(date1.getTime() - date2.getTime());

  // Convert milliseconds to hours, minutes, and seconds
  let hours: number | string = Math.floor(diff / 3600000)
  let minutes: number | string = Math.floor((diff % 3600000) / 60000);
  let seconds: number | string = Math.floor((diff % 60000) / 1000);

  if (minutes < 10) minutes = `0${minutes}`
  if (seconds < 10) seconds = `0${seconds}`
  // Return the result as an object

  return { hours, minutes, seconds };
}

export function getWalkTimeFromCompletedMarkers(completedMarkers: any[]) {
  if (!completedMarkers || completedMarkers.length === 0) {
    return 0; // Return 0 minutes
  }

  // Sort completed markers by arrivedAt timestamp
  const sortedMarkers = [...completedMarkers].sort((a, b) =>
    new Date(a.arrivedAt).getTime() - new Date(b.arrivedAt).getTime()
  );

  // Get first and last marker timestamps
  const firstMarkerTime = new Date(sortedMarkers[0].arrivedAt);
  const lastMarkerTime = new Date(sortedMarkers[sortedMarkers.length - 1].arrivedAt);

  // Calculate time difference in milliseconds
  const diff = Math.abs(lastMarkerTime.getTime() - firstMarkerTime.getTime());

  // Convert to minutes (rounded to nearest minute)
  const minutes = Math.round(diff / 60000);

  return minutes;
}

export function getWalkTimeFromStartFinish(startedAt: string | Date, finishedAt: string | Date) {
  if (!startedAt || !finishedAt) {
    return 0; // Return 0 minutes
  }

  const startTime = new Date(startedAt);
  const finishTime = new Date(finishedAt);

  // Calculate time difference in milliseconds
  const diff = Math.abs(finishTime.getTime() - startTime.getTime());

  // Convert to minutes (rounded to nearest minute)
  const minutes = Math.round(diff / 60000);

  return minutes;
}

// Helper function to format walk time with completion check
export function formatWalkTimeWithCompletion(minutes: number): string {
  if (minutes < 0) {
    return "Not Completed";
  }
  if (minutes === 0) {
    return "< 1 min";
  }
  return `${minutes} min`;
}

/** Table + detail: use server finishedAt when present (authoritative), else marker timestamps. */
export function getDisplayWalkTotalTime(walkReport: {
  startedAt: Date | string;
  finishedAt?: Date | string | null;
  CompletedMarker?: any[];
  walk?: { markers?: any[] };
}): string {
  if (walkReport.finishedAt && walkReport.startedAt) {
    const minutes = getWalkTimeFromStartFinish(
      walkReport.startedAt,
      walkReport.finishedAt,
    );
    return formatWalkTimeWithCompletion(minutes);
  }
  if (walkReport.CompletedMarker && walkReport.CompletedMarker.length > 0) {
    const minutes = getWalkTimeFromCompletedMarkers(walkReport.CompletedMarker);
    return formatWalkTimeWithCompletion(minutes);
  }
  return "Not Completed";
}

export function validatePasswords(password: string, confirmPassword: string) {
  if (password !== confirmPassword) {
    return false
  } else if (password.length < 6) {
    return false
  }
  return true
}

// Test function to demonstrate walk time calculation from your data
export function testWalkTimeCalculation(walkReportData: any) {
  if (!walkReportData.CompletedMarker || walkReportData.CompletedMarker.length === 0) {
    console.log("No completed markers found");
    return null;
  }

  // Get total number of markers from the walk data
  const totalMarkers = walkReportData.walk?.markers?.length || 0;
  const minutesFromMarkers = getWalkTimeFromCompletedMarkers(walkReportData.CompletedMarker);

  console.log("Total markers in walk:", totalMarkers);
  console.log("Completed markers:", walkReportData.CompletedMarker.length);
  console.log("Completion percentage:", totalMarkers > 0 ? ((walkReportData.CompletedMarker.length / totalMarkers) * 100).toFixed(1) + "%" : "N/A");
  console.log("Walk time calculated from completed markers:", formatWalkTimeWithCompletion(minutesFromMarkers));

  // Also show the traditional calculation for comparison
  if (walkReportData.startedAt && walkReportData.finishedAt) {
    const traditionalMinutes = getWalkTimeFromStartFinish(walkReportData.startedAt, walkReportData.finishedAt);
    console.log("Traditional walk time (startedAt to finishedAt):", traditionalMinutes, "minutes");
  }

  return minutesFromMarkers;
}